<template>
  <div class="foil-case" :class="{ empty: slots.length === 0 }">
    <p v-if="slots.length === 0" class="empty-copy">{{ emptyText }}</p>
    <div v-else class="case-grid" :class="{ solo: slots.length === 1 }">
      <article
        v-for="(slot, index) in slots"
        :key="`${slot.cardId}:${slot.foil}:${index}`"
        class="case-slot"
        :class="[
          `foil-${slot.foil}`,
          { featured: index === 0 && slots.length > 1 },
        ]"
      >
        <button
          v-if="interactive"
          type="button"
          class="slot-hit"
          @click="emit('pick', index)"
        >
          <span class="slot-art">
            <CardArt
              :card-id="slot.cardId"
              :foil="slot.foil"
              :alt="nameOf(slot.cardId)"
              :intensity="index === 0 ? 'full' : 'thumb'"
            />
          </span>
        </button>
        <div v-else class="slot-hit static">
          <span class="slot-art">
            <CardArt
              :card-id="slot.cardId"
              :foil="slot.foil"
              :alt="nameOf(slot.cardId)"
              :intensity="index === 0 ? 'full' : 'thumb'"
            />
          </span>
        </div>
        <p class="slot-name">{{ nameOf(slot.cardId) }}</p>
        <p class="slot-foil">{{ t(`packs.rarity.${slot.foil}`) }}</p>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProfileShowcaseSlot } from '@shared/index'
import CardArt from './CardArt.vue'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'

const props = withDefaults(
  defineProps<{
    slots: ProfileShowcaseSlot[]
    emptyText: string
    interactive?: boolean
  }>(),
  { interactive: false },
)

const emit = defineEmits<{
  pick: [index: number]
}>()

const { t } = useI18n()

function nameOf(cardId: string): string {
  return localizedName(cardId)
}
</script>

<style scoped>
.foil-case {
  padding: 18px 16px 20px;
  border-radius: 16px;
  background:
    radial-gradient(ellipse at 50% 0%, rgba(232, 197, 71, 0.16), transparent 55%),
    linear-gradient(180deg, #1a1520 0%, #0e0c12 100%);
  box-shadow:
    inset 0 0 0 1px rgba(232, 197, 71, 0.28),
    0 12px 32px rgba(0, 0, 0, 0.35);
}

.foil-case.empty {
  min-height: 140px;
  display: grid;
  place-items: center;
}

.empty-copy {
  margin: 0;
  padding: 12px 16px;
  color: #d4c4a8;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.case-grid {
  --case-cols: 3;
  --case-gap: 12px;
  --case-slot: calc(
    (100% - (var(--case-cols) - 1) * var(--case-gap)) / var(--case-cols)
  );
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  column-gap: var(--case-gap);
  row-gap: 14px;
}

.case-slot {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 0 0 calc(var(--case-slot) - 0.1px);
  width: calc(var(--case-slot) - 0.1px);
  max-width: var(--case-slot);
  min-width: 0;
}

.case-grid.solo .case-slot {
  flex: 0 1 auto;
  width: min(220px, 100%);
  max-width: min(220px, 100%);
}

.case-slot.featured {
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  align-items: center;
}

.slot-hit {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  line-height: 0;
}

.case-slot.featured .slot-hit,
.case-slot.featured .slot-name,
.case-slot.featured .slot-foil {
  width: var(--case-slot);
}

.slot-art {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 421 / 614;
}

.slot-art :deep(.card-art) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.slot-hit.static {
  cursor: default;
}

.foil-helios .slot-hit,
.foil-nightglass .slot-hit,
.foil-stormwake .slot-hit {
  box-shadow:
    0 0 0 1px rgba(250, 204, 21, 0.45),
    0 10px 24px rgba(232, 197, 71, 0.28);
}

.slot-name,
.slot-foil {
  width: 100%;
  margin: 8px 0 0;
  text-align: center;
  line-height: 1.25;
}

.slot-name {
  color: #f8f1e3;
  font-size: 12px;
  font-weight: 600;
}

.slot-foil {
  margin-top: 2px;
  color: #e8c547;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@media (max-width: 720px) {
  .case-grid {
    --case-cols: 2;
  }

  .case-slot.featured {
    flex: 0 0 calc(var(--case-slot) - 0.1px);
    width: calc(var(--case-slot) - 0.1px);
    max-width: var(--case-slot);
    align-items: stretch;
  }

  .case-slot.featured .slot-hit,
  .case-slot.featured .slot-name,
  .case-slot.featured .slot-foil {
    width: 100%;
  }
}
</style>
